import api from './api';

export interface UserWithStats {
  _id: string;
  email: string;
  role: 'admin' | 'seller';
  firebaseUid: string;
  profile?: {
    name?: string;
    phone?: string;
    company?: string;
  };
  createdAt: Date;
  updatedAt: Date;
  totalEvents: number;
  totalTickets: number;
  totalSales: number;
  eventsThisMonth: number;
  salesThisMonth: number;
  revenueThisMonth: number;
  revenueLastMonth: number;
  revenueGrowth: number;
  currentInventory: number;
  lastLogin: Date;
  isActive: boolean;
}

export interface SystemStats {
  totalUsers: number;
  activeUsers: number;
  totalEvents: number;
  totalSales: number;
  totalRevenue: number;
  revenueThisMonth: number;
  usersThisMonth: number;
  salesThisMonth: number;
  topUsers: Array<{
    _id: string;
    totalRevenue: number;
    salesCount: number;
    user: {
      email: string;
      profile?: { name?: string };
    };
  }>;
}

export interface UserActivity {
  dailyActivity: Array<{
    date: string;
    salesCount: number;
    revenue: number;
    eventsCreated: number;
  }>;
  recentSales: Array<{
    _id: string;
    totalAmount: number;
    saleDate: Date;
    status: string;
    source?: string;
    eventId?: {
      name: string;
      venue: string;
      date: Date;
    };
  }>;
  recentEvents: Array<{
    _id: string;
    name: string;
    venue: string;
    date: Date;
    status: string;
    createdAt: Date;
  }>;
}

export interface UserInventory {
  totalInventoryValue: number;
  availableTickets: number;
  soldTickets: number;
  reservedTickets: number;
  totalRevenue: number;
  totalProfit: number;
  profitMargin: number;
  topCategories: Array<{
    category: string;
    count: number;
    value: number;
  }>;
  recentTickets: Array<{
    _id: string;
    eventName: string;
    category?: string;
    section?: string;
    quantity: number;
    purchasePrice: number;
    status: string;
    createdAt: Date;
    eventId?: {
      name: string;
      venue: string;
      date: Date;
    };
  }>;
  summary: {
    totalTickets: number;
    totalSales: number;
    averageTicketPrice: number;
    turnoverRate: number;
  };
}

export interface UserTickets {
  tickets: Array<{
    _id: string;
    eventName: string;
    eventDate: Date;
    venue: string;
    section?: string;
    category?: string;
    block?: string;
    row?: string;
    seat?: string;
    quantity: number;
    soldQuantity?: number;
    purchasePrice: number;
    wantedPrice?: number;
    status: string;
    sourceType?: string;
    createdAt: Date;
    eventId?: {
      name: string;
      venue: string;
      date: Date;
      status: string;
    };
    supplierId?: {
      name: string;
      email?: string;
    };
  }>;
  total: number;
}

export interface InventoryOverview {
  overview: {
    totalTickets: number;
    availableTickets: number;
    soldTickets: number;
    reservedTickets: number;
    totalInventoryValue: number;
    totalRevenue: number;
    averageTicketValue: number;
  };
  topCategories: Array<{
    category: string;
    count: number;
    value: number;
    userCount: number;
  }>;
  topSuppliers: Array<{
    _id: string;
    ticketCount: number;
    totalValue: number;
    supplier: {
      name: string;
      email?: string;
    };
  }>;
  topUsersByInventory: Array<{
    _id: string;
    totalTickets: number;
    totalValue: number;
    user: {
      email: string;
      profile?: { name?: string };
    };
  }>;
}

class AdminApiService {
  async getAllUsers(): Promise<UserWithStats[]> {
    const response = await api.get('/admin/users');
    return response.data;
  }

  async getUser(userId: string): Promise<UserWithStats> {
    const response = await api.get(`/admin/users/${userId}`);
    return response.data;
  }

  async getUserStats(userId: string) {
    const response = await api.get(`/admin/users/${userId}/stats`);
    return response.data;
  }

  async getUserActivity(userId: string): Promise<UserActivity> {
    const response = await api.get(`/admin/users/${userId}/activity`);
    return response.data;
  }

  async getSystemStats(): Promise<SystemStats> {
    const response = await api.get('/admin/system/stats');
    return response.data;
  }

  async updateUserRole(userId: string, role: 'admin' | 'seller') {
    const response = await api.patch(`/admin/users/${userId}/role`, { role });
    return response.data;
  }

  async deleteUser(userId: string) {
    const response = await api.delete(`/admin/users/${userId}`);
    return response.data;
  }

  async getUserInventory(userId: string): Promise<UserInventory> {
    const response = await api.get(`/admin/users/${userId}/inventory`);
    return response.data;
  }

  async getUserTickets(userId: string): Promise<UserTickets> {
    const response = await api.get(`/admin/users/${userId}/tickets`);
    return response.data;
  }

  async getInventoryOverview(): Promise<InventoryOverview> {
    const response = await api.get('/admin/inventory/overview');
    return response.data;
  }
}

export const adminApiService = new AdminApiService();